import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { BarChart3, Check, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const PollDisplay = ({ post, onVote }) => {
  const { user, api } = useAuth();
  const [selectedOption, setSelectedOption] = useState(null);
  const [voting, setVoting] = useState(false);
  const [pollData, setPollData] = useState({
    options: post.poll_options || [],
    totalVotes: post.poll_total_votes || 0,
    userVote: post.user_poll_vote
  });
  
  const isExpired = post.poll_end_date && new Date(post.poll_end_date) < new Date();
  const hasVoted = pollData.userVote !== null && pollData.userVote !== undefined;
  
  useEffect(() => {
    if (hasVoted) {
      setSelectedOption(pollData.userVote);
    }
  }, [hasVoted, pollData.userVote]);
  
  const handleVote = async () => {
    if (!user) {
      toast.error('Connectez-vous pour voter');
      return;
    }
    
    if (selectedOption === null) {
      toast.error('Sélectionnez une option');
      return;
    }
    
    setVoting(true);
    try {
      const response = await api.post(`/posts/${post.post_id}/vote`, {
        option_index: selectedOption
      });
      
      setPollData({
        options: response.data.poll_options,
        totalVotes: response.data.poll_total_votes,
        userVote: response.data.user_vote
      });
      
      toast.success('Vote enregistré !');
      onVote?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du vote');
    } finally {
      setVoting(false);
    }
  };
  
  const getPercentage = (votes) => {
    if (pollData.totalVotes === 0) return 0;
    return Math.round((votes / pollData.totalVotes) * 100);
  };

  // Show results view if voted or expired
  if (hasVoted || isExpired) {
    return (
      <div className="mt-4 space-y-3" data-testid={`poll-results-${post.post_id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Résultats du sondage</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {pollData.totalVotes} vote{pollData.totalVotes !== 1 ? 's' : ''}
          </Badge>
        </div>
        
        <div className="space-y-2">
          {pollData.options.map((option, index) => {
            const percentage = getPercentage(option.votes);
            const isUserVote = pollData.userVote === index;
            
            return (
              <div key={index} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className={isUserVote ? 'font-medium' : ''}>
                      {option.text}
                    </span>
                    {isUserVote && (
                      <Check className="h-3 w-3 text-primary" />
                    )}
                  </div>
                  <span className="text-muted-foreground">{percentage}%</span>
                </div>
                <Progress 
                  value={percentage} 
                  className={`h-2 ${isUserVote ? '[&>div]:bg-primary' : ''}`}
                />
              </div>
            );
          })}
        </div>
        
        {isExpired && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Sondage terminé</span>
          </div>
        )}
        
        {!isExpired && post.poll_end_date && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              Fin {formatDistanceToNow(new Date(post.poll_end_date), { addSuffix: true, locale: fr })}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Show voting view
  return (
    <div className="mt-4 space-y-3" data-testid={`poll-vote-${post.post_id}`}>
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Sondage</span>
      </div>
      
      <RadioGroup 
        value={selectedOption?.toString()} 
        onValueChange={(val) => setSelectedOption(parseInt(val))}
        className="space-y-2"
      >
        {pollData.options.map((option, index) => (
          <div 
            key={index} 
            className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer"
            onClick={() => setSelectedOption(index)}
          >
            <RadioGroupItem value={index.toString()} id={`option-${post.post_id}-${index}`} />
            <Label 
              htmlFor={`option-${post.post_id}-${index}`}
              className="flex-1 cursor-pointer"
            >
              {option.text}
            </Label>
          </div>
        ))}
      </RadioGroup>
      
      <Button 
        onClick={handleVote}
        disabled={selectedOption === null || voting}
        className="w-full"
        data-testid={`vote-btn-${post.post_id}`}
      >
        {voting ? 'Vote en cours...' : 'Voter'}
      </Button>
      
      {post.poll_end_date && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            Fin {formatDistanceToNow(new Date(post.poll_end_date), { addSuffix: true, locale: fr })}
          </span>
        </div>
      )}
    </div>
  );
};

export default PollDisplay;
